import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Heart, Loader2, Lock, XCircle, Sun, Moon, Users, Target, CheckCircle, Home, Bitcoin, CreditCard } from 'lucide-react';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const LOGO_URL = "/holapay-mark.svg";

// Gateway logos
const gatewayLogos = {
  paypal: 'https://www.paypalobjects.com/webstatic/mktg/logo/pp_cc_mark_111x69.jpg',
  viva_wallet: 'https://www.vivawallet.com/assets/images/logo.svg',
  roxpay: null,
  mollie: null,
  crypto: 'https://upload.wikimedia.org/wikipedia/commons/4/46/Bitcoin.svg',
  nexi: 'https://www.nexi.it/content/dam/nexi/loghi/logo-nexi.svg',
  payplug: null,
  sumup: null,
  silkpay: null
};

// OG Meta translations
const ogTranslations = {
  it: { donateNow: "Dona Ora", donationFor: "Donazione per" },
  en: { donateNow: "Donate Now", donationFor: "Donation for" },
  de: { donateNow: "Jetzt Spenden", donationFor: "Spende für" },
  es: { donateNow: "Donar Ahora", donationFor: "Donación para" },
  fr: { donateNow: "Faire un Don", donationFor: "Don pour" },
  ar: { donateNow: "تبرع الآن", donationFor: "تبرع لـ" },
  ru: { donateNow: "Пожертвовать", donationFor: "Пожертвование для" }
};

// Translations for donation page
const donationTranslations = {
  it: {
    badge: "Donazione",
    causeNotFound: "Causa non trovata",
    causeNotFoundDesc: "Questa causa di donazione non esiste o non è più attiva.",
    goal: "Obiettivo",
    reached: "raggiunto",
    donors: "donatori",
    selectAmount: "Seleziona un importo",
    customAmount: "Inserisci un importo",
    anonymous: "Dona in modo anonimo",
    yourName: "Il tuo nome",
    namePlaceholder: "Mario Rossi",
    yourEmail: "La tua email",
    emailPlaceholder: "mario@esempio.it",
    emailRequired: "L'email è obbligatoria per le donazioni non anonime",
    message: "Messaggio (opzionale)",
    messagePlaceholder: "Lascia un messaggio di supporto...",
    donateNow: "Dona Ora",
    processing: "Elaborazione...",
    secure: "Pagamento sicuro e protetto",
    poweredBy: "Gestito da",
    error: "Errore durante la donazione. Riprova.",
    paymentMethod: "Metodo di pagamento"
  },
  en: {
    badge: "Donation",
    causeNotFound: "Cause not found",
    causeNotFoundDesc: "This donation cause does not exist or is no longer active.",
    goal: "Goal",
    reached: "reached",
    donors: "donors",
    selectAmount: "Select an amount",
    customAmount: "Enter custom amount",
    anonymous: "Donate anonymously",
    yourName: "Your name",
    namePlaceholder: "John Doe",
    yourEmail: "Your email",
    emailPlaceholder: "john@example.com",
    emailRequired: "Email is required for non-anonymous donations",
    message: "Message (optional)",
    messagePlaceholder: "Leave a message of support...",
    donateNow: "Donate Now",
    processing: "Processing...",
    secure: "Secure and protected payment",
    poweredBy: "Powered by",
    error: "Error processing donation. Please try again.",
    paymentMethod: "Payment method"
  },
  de: {
    badge: "Spende",
    causeNotFound: "Ursache nicht gefunden",
    causeNotFoundDesc: "Diese Spendenursache existiert nicht oder ist nicht mehr aktiv.",
    goal: "Ziel",
    reached: "erreicht",
    donors: "Spender",
    selectAmount: "Betrag auswählen",
    customAmount: "Eigenen Betrag eingeben",
    anonymous: "Anonym spenden",
    yourName: "Ihr Name",
    namePlaceholder: "Max Mustermann",
    yourEmail: "Ihre E-Mail",
    emailPlaceholder: "max@beispiel.de",
    emailRequired: "E-Mail ist für nicht-anonyme Spenden erforderlich",
    message: "Nachricht (optional)",
    messagePlaceholder: "Hinterlassen Sie eine Unterstützungsnachricht...",
    donateNow: "Jetzt Spenden",
    processing: "Verarbeitung...",
    secure: "Sichere und geschützte Zahlung",
    poweredBy: "Bereitgestellt von",
    error: "Fehler bei der Spende. Bitte versuchen Sie es erneut.",
    paymentMethod: "Zahlungsmethode"
  },
  es: {
    badge: "Donación",
    causeNotFound: "Causa no encontrada",
    causeNotFoundDesc: "Esta causa de donación no existe o ya no está activa.",
    goal: "Objetivo",
    reached: "alcanzado",
    donors: "donantes",
    selectAmount: "Selecciona un monto",
    customAmount: "Ingresa un monto personalizado",
    anonymous: "Donar de forma anónima",
    yourName: "Tu nombre",
    namePlaceholder: "Juan García",
    yourEmail: "Tu correo electrónico",
    emailPlaceholder: "juan@ejemplo.es",
    emailRequired: "El correo es obligatorio para donaciones no anónimas",
    message: "Mensaje (opcional)",
    messagePlaceholder: "Deja un mensaje de apoyo...",
    donateNow: "Donar Ahora",
    processing: "Procesando...",
    secure: "Pago seguro y protegido",
    poweredBy: "Gestionado por",
    error: "Error al procesar la donación. Inténtalo de nuevo.",
    paymentMethod: "Método de pago"
  },
  fr: {
    badge: "Don",
    causeNotFound: "Cause non trouvée",
    causeNotFoundDesc: "Cette cause de don n'existe pas ou n'est plus active.",
    goal: "Objectif",
    reached: "atteint",
    donors: "donateurs",
    selectAmount: "Sélectionnez un montant",
    customAmount: "Entrez un montant personnalisé",
    anonymous: "Faire un don anonyme",
    yourName: "Votre nom",
    namePlaceholder: "Jean Dupont",
    yourEmail: "Votre e-mail",
    emailPlaceholder: "jean@exemple.fr",
    emailRequired: "L'e-mail est requis pour les dons non anonymes",
    message: "Message (optionnel)",
    messagePlaceholder: "Laissez un message de soutien...",
    donateNow: "Faire un Don",
    processing: "Traitement...",
    secure: "Paiement sécurisé et protégé",
    poweredBy: "Géré par",
    error: "Erreur lors du don. Veuillez réessayer.",
    paymentMethod: "Méthode de paiement"
  },
  ar: {
    badge: "تبرع",
    causeNotFound: "السبب غير موجود",
    causeNotFoundDesc: "هذا السبب للتبرع غير موجود أو لم يعد نشطًا.",
    goal: "الهدف",
    reached: "تم الوصول",
    donors: "متبرعين",
    selectAmount: "اختر مبلغًا",
    customAmount: "أدخل مبلغًا مخصصًا",
    anonymous: "تبرع بشكل مجهول",
    yourName: "اسمك",
    namePlaceholder: "أحمد محمد",
    yourEmail: "بريدك الإلكتروني",
    emailPlaceholder: "ahmed@example.com",
    emailRequired: "البريد الإلكتروني مطلوب للتبرعات غير المجهولة",
    message: "رسالة (اختياري)",
    messagePlaceholder: "اترك رسالة دعم...",
    donateNow: "تبرع الآن",
    processing: "جاري المعالجة...",
    secure: "دفع آمن ومحمي",
    poweredBy: "مدعوم من",
    error: "خطأ في معالجة التبرع. حاول مرة أخرى.",
    paymentMethod: "طريقة الدفع"
  },
  ru: {
    badge: "Пожертвование",
    causeNotFound: "Причина не найдена",
    causeNotFoundDesc: "Эта причина для пожертвования не существует или больше не активна.",
    goal: "Цель",
    reached: "достигнуто",
    donors: "жертвователей",
    selectAmount: "Выберите сумму",
    customAmount: "Введите свою сумму",
    anonymous: "Пожертвовать анонимно",
    yourName: "Ваше имя",
    namePlaceholder: "Иван Иванов",
    yourEmail: "Ваш email",
    emailPlaceholder: "ivan@example.com",
    emailRequired: "Email обязателен для неанонимных пожертвований",
    message: "Сообщение (необязательно)",
    messagePlaceholder: "Оставьте сообщение поддержки...",
    donateNow: "Пожертвовать",
    processing: "Обработка...",
    secure: "Безопасный и защищённый платёж",
    poweredBy: "При поддержке",
    error: "Ошибка при пожертвовании. Попробуйте снова.",
    paymentMethod: "Способ оплаты"
  }
};

export default function DonationPage() {
  const { causeId } = useParams();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  
  // Get merchant ID from URL query parameter (?merchant=xxx)
  const searchParams = new URLSearchParams(window.location.search);
  const merchantParam = searchParams.get('merchant');
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [merchantLang, setMerchantLang] = useState('it');
  
  // Form state
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [message, setMessage] = useState('');
  const [selectedGateway, setSelectedGateway] = useState(null);

  // Get translations based on merchant language
  const t = useMemo(() => {
    return donationTranslations[merchantLang] || donationTranslations.it;
  }, [merchantLang]);

  useEffect(() => {
    fetchCause();
  }, [causeId, merchantParam]);

  const fetchCause = async () => {
    try {
      // Include merchant parameter if provided in URL
      const url = merchantParam 
        ? `${API_URL}/api/donate/${causeId}?merchant=${merchantParam}`
        : `${API_URL}/api/donate/${causeId}`;
      const response = await axios.get(url);
      setData(response.data);
      
      // Set merchant language
      if (response.data.merchant?.language) {
        setMerchantLang(response.data.merchant.language);
      }
      
      // Pre-select first suggested amount
      if (response.data.cause?.suggested_amounts?.length > 0) {
        setSelectedAmount(response.data.cause.suggested_amounts[0]);
      }
      
      // Pre-select first gateway if available
      if (response.data.gateways?.length > 0) {
        setSelectedGateway(response.data.gateways[0]);
      }
    } catch (error) {
      console.error('Error fetching cause:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAmount = () => {
    if (selectedAmount) return selectedAmount;
    if (customAmount) return parseFloat(customAmount);
    return 0;
  };

  const handleDonate = async () => {
    const amount = getAmount();
    
    if (!amount || amount <= 0) {
      toast.error(t.selectAmount);
      return;
    }
    
    // Validate email for non-anonymous donations
    if (!isAnonymous && !donorEmail) {
      toast.error(t.emailRequired);
      return;
    }
    
    setProcessing(true);
    
    try {
      const response = await axios.post(`${API_URL}/api/donate/${causeId}/create-payment`, {
        cause_id: causeId,
        amount: amount,
        donor_name: isAnonymous ? null : donorName,
        donor_email: isAnonymous ? null : donorEmail,
        is_anonymous: isAnonymous,
        message: message || null
      }, {
        params: {
          selected_gateway: selectedGateway?.name,
          selected_gateway_id: selectedGateway?.id,
          // CRITICAL: Pass merchant ID for multi-merchant causes
          selected_merchant_id: merchantParam || data?.merchant?.id
        }
      });
      
      if (response.data.payment_url) {
        // Redirect to payment page
        window.location.href = response.data.payment_url;
      } else {
        toast.error(t.error);
        setProcessing(false);
      }
    } catch (error) {
      console.error('Donation error:', error);
      toast.error(error.response?.data?.detail || t.error);
      setProcessing(false);
    }
  };

  const formatCurrency = (amount, currency) => {
    const locale = merchantLang === 'de' ? 'de-DE' : merchantLang === 'en' ? 'en-US' : merchantLang === 'es' ? 'es-ES' : merchantLang === 'fr' ? 'fr-FR' : 'it-IT';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency || 'EUR'
    }).format(amount || 0);
  };

  const calculateProgress = () => {
    if (!data?.cause?.goal_amount || data.cause.goal_amount === 0) return 0;
    return Math.min(100, (data.cause.total_raised / data.cause.goal_amount) * 100);
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0B0F2A]' : 'bg-slate-50'}`}>
        <Loader2 className={`w-8 h-8 animate-spin ${isDark ? 'text-[#9D4DFF]' : 'text-primary'}`} />
      </div>
    );
  }

  if (!data || !data.cause) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0B0F2A]' : 'bg-slate-50'}`}>
        <Card className={`max-w-md w-full mx-4 ${isDark ? 'bg-[#0F0F2F] border-purple-500/30' : ''}`}>
          <CardContent className="p-8 text-center">
            <XCircle className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-red-400' : 'text-red-500'}`} />
            <h1 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {t.causeNotFound}
            </h1>
            <p className={isDark ? 'text-gray-400' : 'text-slate-600'}>
              {t.causeNotFoundDesc}
            </p>
            <Button
              onClick={() => navigate('/')}
              className="mt-6"
              variant="outline"
            >
              <Home className="w-4 h-4 mr-2" />
              Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { cause, merchant, gateways, branding } = data;
  const primaryColor = branding?.primary_color || '#6B46C1';
  const progress = calculateProgress();
  
  // Get OG translations based on merchant language
  const ogT = ogTranslations[merchantLang] || ogTranslations.it;
  const ogTitle = `${cause.title} - ${ogT.donateNow}`;
  const ogDescription = cause.description || `${ogT.donationFor} ${cause.title}`;
  const ogImage = cause.image_url || branding?.logo_url || LOGO_URL;

  return (
    <div className={`hola-public-page min-h-screen ${isDark ? 'bg-[#0B0F2A]' : 'bg-gradient-to-br from-purple-50 to-indigo-100'}`}>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{ogTitle}</title>
        <meta name="description" content={ogDescription} />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ogTitle} />
        <meta name="twitter:description" content={ogDescription} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>
      
      {/* Header */}
      <div className={`border-b ${isDark ? 'bg-[#0F0F2F]/80 border-purple-500/20' : 'bg-white/80 border-slate-200'} backdrop-blur-sm sticky top-0 z-10`}>
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src={branding?.logo_url || LOGO_URL} 
              alt="Logo" 
              className="h-10 w-10 object-contain rounded-lg"
            />
            <span className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {branding?.business_name || 'Hola Pay'}
            </span>
          </div>
          <button
            onClick={toggleTheme}
            className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-purple-500/10 text-gray-400' : 'hover:bg-slate-100 text-slate-600'}`}
          >
            {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left: Cause Info */}
          <div>
            <Badge 
              className="mb-4" 
              style={{ backgroundColor: primaryColor }}
            >
              <Heart className="w-3 h-3 mr-1" />
              {t.badge}
            </Badge>
            
            <h1 className={`text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {cause.title}
            </h1>
            
            {cause.description && (
              <p className={`text-lg mb-6 ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                {cause.description}
              </p>
            )}

            {/* Progress Bar (if goal is set) */}
            {cause.goal_amount > 0 && (
              <div className={`p-6 rounded-xl mb-6 ${isDark ? 'bg-[#0F0F2F] border border-purple-500/20' : 'bg-white shadow-lg'}`}>
                <div className="flex justify-between items-center mb-2">
                  <span className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                    {t.goal}: {formatCurrency(cause.goal_amount, cause.currency)}
                  </span>
                  <span className={`text-sm font-medium ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                    {progress.toFixed(0)}% {t.reached}
                  </span>
                </div>
                <div className={`h-3 rounded-full overflow-hidden ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`}>
                  <div 
                    className="h-full rounded-full transition-all duration-500"
                    style={{ 
                      width: `${progress}%`,
                      backgroundColor: primaryColor
                    }}
                  />
                </div>
                <div className="flex justify-between items-center mt-4">
                  <div className="flex items-center gap-2">
                    <Target className={`w-5 h-5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                    <span className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {formatCurrency(cause.total_raised || 0, cause.currency)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-slate-500'}`} />
                    <span className={isDark ? 'text-gray-400' : 'text-slate-500'}>
                      {cause.donation_count || 0} {t.donors}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Cause Image */}
            {cause.image_url && (
              <img 
                src={cause.image_url} 
                alt={cause.title}
                className="w-full rounded-xl object-cover max-h-64 mb-6"
              />
            )}

            {/* Merchant Info */}
            {merchant && (
              <div className={`flex items-center gap-3 p-4 rounded-lg ${isDark ? 'bg-purple-500/10' : 'bg-purple-50'}`}>
                <CheckCircle className={`w-5 h-5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                <span className={isDark ? 'text-gray-300' : 'text-slate-700'}>
                  {t.poweredBy} <strong>{merchant.business_name}</strong>
                </span>
              </div>
            )}
          </div>

          {/* Right: Donation Form */}
          <div>
            <Card className={`${isDark ? 'bg-[#0F0F2F] border-purple-500/30' : 'shadow-xl'}`}>
              <CardContent className="p-6 space-y-6">
                {/* Amount Selection */}
                <div>
                  <Label className={`text-sm font-medium mb-3 block ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                    {t.selectAmount}
                  </Label>
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {cause.suggested_amounts?.map((amount) => (
                      <button
                        key={amount}
                        onClick={() => {
                          setSelectedAmount(amount);
                          setCustomAmount('');
                        }}
                        className={`p-3 rounded-lg font-semibold transition-all ${
                          selectedAmount === amount
                            ? isDark 
                              ? 'bg-purple-600 text-white' 
                              : 'bg-purple-600 text-white'
                            : isDark
                              ? 'bg-slate-800 text-gray-300 hover:bg-slate-700'
                              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                      >
                        {formatCurrency(amount, cause.currency)}
                      </button>
                    ))}
                  </div>
                  
                  {cause.allow_custom_amount && (
                    <div className="relative">
                      <Input
                        type="number"
                        placeholder={t.customAmount}
                        value={customAmount}
                        onChange={(e) => {
                          setCustomAmount(e.target.value);
                          setSelectedAmount(null);
                        }}
                        min={cause.min_amount}
                        max={cause.max_amount}
                        className={isDark ? 'bg-slate-800 border-slate-700 text-white' : ''}
                      />
                      <span className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                        {cause.currency}
                      </span>
                    </div>
                  )}
                </div>

                {/* Anonymous Toggle */}
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="anonymous"
                    checked={isAnonymous}
                    onCheckedChange={setIsAnonymous}
                  />
                  <Label 
                    htmlFor="anonymous" 
                    className={`cursor-pointer ${isDark ? 'text-gray-300' : 'text-slate-700'}`}
                  >
                    {t.anonymous}
                  </Label>
                </div>

                {/* Donor Info (if not anonymous) */}
                {!isAnonymous && (
                  <div className="space-y-4">
                    <div>
                      <Label className={`text-sm mb-1 block ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                        {t.yourName}
                      </Label>
                      <Input
                        placeholder={t.namePlaceholder}
                        value={donorName}
                        onChange={(e) => setDonorName(e.target.value)}
                        className={isDark ? 'bg-slate-800 border-slate-700 text-white' : ''}
                      />
                    </div>
                    <div>
                      <Label className={`text-sm mb-1 block ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                        {t.yourEmail} *
                      </Label>
                      <Input
                        type="email"
                        placeholder={t.emailPlaceholder}
                        value={donorEmail}
                        onChange={(e) => setDonorEmail(e.target.value)}
                        className={isDark ? 'bg-slate-800 border-slate-700 text-white' : ''}
                        required
                      />
                    </div>
                  </div>
                )}

                {/* Message */}
                <div>
                  <Label className={`text-sm mb-1 block ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                    {t.message}
                  </Label>
                  <Textarea
                    placeholder={t.messagePlaceholder}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={3}
                    className={isDark ? 'bg-slate-800 border-slate-700 text-white' : ''}
                  />
                </div>

                {/* Gateway Selection (if multiple) */}
                {gateways?.length > 1 && (
                  <div>
                    <Label className={`text-sm mb-2 block ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                      {t.paymentMethod}
                    </Label>
                    <div className="grid grid-cols-2 gap-3">
                      {gateways.map((gw) => (
                        <button
                          key={gw.id}
                          onClick={() => setSelectedGateway(gw)}
                          className={`p-4 rounded-xl flex flex-col items-center justify-center gap-2 transition-all min-h-[80px] ${
                            selectedGateway?.id === gw.id
                              ? isDark 
                                ? 'bg-purple-600/20 border-2 border-purple-500' 
                                : 'bg-purple-50 border-2 border-purple-500'
                              : isDark
                                ? 'bg-slate-800 border border-slate-700 hover:bg-slate-700'
                                : 'bg-white border border-slate-200 hover:bg-slate-50'
                          }`}
                        >
                          {gw.name === 'paypal' ? (
                            <img 
                              src={gatewayLogos['paypal']} 
                              alt="PayPal" 
                              className="h-8 object-contain"
                            />
                          ) : gw.name === 'crypto' || gw.name === 'nowpayments' ? (
                            <>
                              <Bitcoin className="w-7 h-7 text-orange-500" />
                              <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                                Crypto
                              </span>
                            </>
                          ) : gw.name === 'mollie' ? (
                            <>
                              <CreditCard className={`w-7 h-7 ${isDark ? 'text-gray-400' : 'text-slate-600'}`} />
                              <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                                {merchantLang === 'it' ? 'Carta di Credito/Debito' : 'Credit/Debit Card'}
                              </span>
                            </>
                          ) : (
                            <>
                              <CreditCard className={`w-7 h-7 ${isDark ? 'text-gray-400' : 'text-slate-600'}`} />
                              <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                                {merchantLang === 'it' ? 'Carta di Credito/Debito' : 'Credit/Debit Card'}
                              </span>
                            </>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Donate Button */}
                <Button
                  onClick={handleDonate}
                  disabled={processing || getAmount() <= 0}
                  className="w-full h-12 text-lg font-semibold"
                  style={{ backgroundColor: primaryColor }}
                  data-testid="donate-button"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      {t.processing}
                    </>
                  ) : (
                    <>
                      <Heart className="w-5 h-5 mr-2" />
                      {t.donateNow} {getAmount() > 0 && formatCurrency(getAmount(), cause.currency)}
                    </>
                  )}
                </Button>

                {/* Security Note */}
                <div className={`flex items-center justify-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                  <Lock className="w-4 h-4" />
                  {t.secure}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className={`py-6 text-center ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
        <p className="text-sm">
          {t.poweredBy} <strong>Hola Pay</strong>
        </p>
      </div>
    </div>
  );
}
